import React from 'react';
import Icon from '../../../components/AppIcon';

const CalorieProgressRing = ({ consumed, burned, goal, protein = 0, carbs = 0, fat = 0, macroTargets }) => {
  const netCalories = consumed - burned;
  const progressPercentage = Math.min((consumed / goal) * 100, 100);
  const circumference = 2 * Math.PI * 45;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (progressPercentage / 100) * circumference;

  // Use user's custom macro targets if provided, otherwise fall back to defaults
  const proteinPct = macroTargets?.protein ?? 25;
  const carbsPct = macroTargets?.carbs ?? 50;
  const fatPct = macroTargets?.fat ?? 25;

  const proteinGoal = Math.round((goal * (proteinPct / 100)) / 4);
  const carbsGoal = Math.round((goal * (carbsPct / 100)) / 4);
  const fatGoal = Math.round((goal * (fatPct / 100)) / 9);

  const getProgressColor = () => {
    if (progressPercentage <= 50) return '#10B981';
    if (progressPercentage <= 80) return '#F59E0B';
    return '#EF4444';
  };

  const MacroBar = ({ label, current, goal, color, bgColor, pct }) => {
    const fillPct = Math.min((current / goal) * 100, 100);
    return (
      <div>
        <div className="flex justify-between text-xs mb-1">
          <span className="text-muted-foreground">{label} <span className="opacity-60">({pct}%)</span></span>
          <span className="font-medium text-foreground">{Math.round(current)}g / {goal}g</span>
        </div>
        <div className={`w-full h-2 rounded-full ${bgColor}`}>
          <div
            className="h-2 rounded-full transition-all duration-500"
            style={{ width: `${fillPct}%`, backgroundColor: color }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="bg-card rounded-xl p-6 elevation-2">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Daily Calories</h3>
        <Icon name="Target" size={20} className="text-muted-foreground" />
      </div>
      
      <div className="flex items-center justify-center mb-6">
        <div className="relative w-32 h-32">
          <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="45" stroke="#E5E7EB" strokeWidth="8" fill="none" />
            <circle
              cx="50" cy="50" r="45"
              stroke={getProgressColor()}
              strokeWidth="8" fill="none" strokeLinecap="round"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-500 ease-out"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-bold text-foreground">{consumed}</span>
            <span className="text-xs text-muted-foreground">consumed</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 text-center mb-6">
        <div>
          <div className="text-sm text-muted-foreground">Goal</div>
          <div className="text-lg font-semibold text-foreground">{goal}</div>
        </div>
        <div>
          <div className="text-sm text-muted-foreground">Burned</div>
          <div className="text-lg font-semibold text-success">{burned}</div>
        </div>
        <div>
          <div className="text-sm text-muted-foreground">Net</div>
          <div className={`text-lg font-semibold ${netCalories > 0 ? 'text-warning' : 'text-success'}`}>
            {netCalories > 0 ? '+' : ''}{netCalories}
          </div>
        </div>
      </div>

      {/* Macronutrient Progress Bars */}
      <div className="space-y-3 pt-4 border-t border-border/40">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Macronutrients</p>
        <MacroBar label="Protein" current={protein} goal={proteinGoal} pct={proteinPct} color="#10B981" bgColor="bg-emerald-100 dark:bg-emerald-900/30" />
        <MacroBar label="Carbs" current={carbs} goal={carbsGoal} pct={carbsPct} color="#3B82F6" bgColor="bg-blue-100 dark:bg-blue-900/30" />
        <MacroBar label="Fat" current={fat} goal={fatGoal} pct={fatPct} color="#F59E0B" bgColor="bg-amber-100 dark:bg-amber-900/30" />
      </div>
    </div>
  );
};

export default CalorieProgressRing;